/**
 * Whether this deployment has the CHAT HOME at all (#368, owner 2026-10-29).
 *
 * A launcher deployment (`experience.landing: launcher`, or the platform
 * setting choosing it) has chat OFF: no chat tab on the phone, no chat deck,
 * and a `/…/chat` URL goes home. There is no separate switch — the landing
 * decides. Only the chat HOME surface goes: the agent API and the workspace's
 * AI panel stay on.
 *
 * A store in @papan/core-ui, beside `keybookAvailability`, for the same reason:
 * the readers span packages that cannot reach each other (the shells in
 * apps/web, Settings' chat storage in features/orgs). The ONE writer is
 * apps/web's landing module, which derives it from the landing it holds —
 * `chatHomeAvailableFor` in apps/web/src/lib/landing.ts is the rule.
 *
 * Seeds `true`, the shipped state of a chat deployment, so a consumer rendered
 * with no landing module (a test) behaves as the product always has.
 * DISCLOSURE only: nothing here is a permission.
 */
import { useSyncExternalStore } from 'react'

let available = true
const listeners = new Set<() => void>()

export function setChatHomeAvailable(v: boolean): void {
  if (v === available) return
  available = v
  for (const l of listeners) l()
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => { listeners.delete(onStoreChange) }
}

function snapshot(): boolean {
  return available
}

export function useChatHomeAvailable(): boolean {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

/** Synchronous read for event handlers and non-React code. */
export function chatHomeAvailable(): boolean {
  return available
}
